import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

import {MERCHANTS_CACHE, MERCHANT_DETAILS_CACHE} from 'constants/cache';
import {useClient} from 'context/auth-context';
import {PAGINATION_LIMIT} from 'constants/common';
import {ITag} from 'types/api';
import {PATCH_HEADERS} from 'api/constants';
import {ExpenseCategory, ExpenseCostCenter} from 'types/api/expenses';
import {IAmnisApiFilters} from 'components/ui/AmnisFilters/types';

export const MERCHANTS_ENDPOINT = '/web_api/merchants';

export interface IMerchant {
    '@id': string;
    '@type': 'Merchant';
    id: number;
    name: string;
    city: string;
    country: string;
    state: string | null;
    tags: ITag[] | null;
    expenseCategory: ExpenseCategory | null;
    expenseCostCenter: ExpenseCostCenter | null;
    expenseVat?: string;
    vat: number | null;
    blocked: boolean;
    autoCompleteExpenseManagement: boolean;
}

const useGetMerchants = () => {
    const client = useClient();

    return useQuery<IMerchant[]>({
        queryKey: [MERCHANTS_CACHE],
        queryFn: () => client(`${MERCHANTS_ENDPOINT}?pagination=false`, {}),
    });
};

interface IUseGetInfiniteMerchantsResponse {
    '@context': string;
    '@id': string;
    '@type': string;
    'hydra:member': IMerchant[];
    'hydra:totalItems': number;
}

const useGetInfiniteMerchants = (
    itemsCount = PAGINATION_LIMIT,
    filters: IAmnisApiFilters,
) => {
    const client = useClient();
    return useInfiniteQuery<IUseGetInfiniteMerchantsResponse>({
        queryKey: [MERCHANTS_CACHE, {itemsCount, filters}],
        queryFn: ({pageParam}) => {
            return client(
                MERCHANTS_ENDPOINT,
                {
                    params: {
                        page: pageParam,
                        itemsPerPage: itemsCount,
                        ...filters,
                        order_card_transaction_count: 'DESC',
                    },
                },
                true,
            );
        },
        initialPageParam: 1,
        getNextPageParam: (lastGroup, allGroups) => {
            if (
                lastGroup['hydra:member'].length === itemsCount &&
                allGroups.length * itemsCount < lastGroup['hydra:totalItems']
            ) {
                return allGroups.length + 1;
            }
            return undefined;
        },
    });
};

export interface IMerchantWithSpending extends IMerchant {
    monthSpending: number;
    yearSpending: number;
}

const useGetMerchantDetails = (merchantId: string) => {
    const client = useClient();

    return useQuery<IMerchantWithSpending>({
        queryKey: [MERCHANT_DETAILS_CACHE, {merchantId}],
        queryFn: () =>
            client(`${MERCHANTS_ENDPOINT}/${merchantId}`, {
                params: {
                    groups: ['MerchantSpendingView'],
                },
            }),
    });
};

export interface IUseUpdateMerchantRequest {
    '@id': string;
    expenseCategory: string;
    expenseCostCenter: string;
    expenseVat?: string | null;
    tags: string[] | null;
    vat: number | null;
}

const useUpdateMerchant = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<IMerchant, unknown, IUseUpdateMerchantRequest>({
        mutationFn: data => {
            const {'@id': iri, ...rest} = data;
            return client(`${iri}`, {
                data: rest,
                method: 'PATCH',
                ...PATCH_HEADERS,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: [MERCHANTS_CACHE]});
            queryClient.invalidateQueries({
                queryKey: [MERCHANT_DETAILS_CACHE],
            });
        },
    });
};

const useToggleBlockMerchant = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<IMerchant, unknown, IMerchant>({
        mutationFn: data =>
            client(`${data['@id']}/toggle_blocked`, {
                data: {},
                method: 'PATCH',
                ...PATCH_HEADERS,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: [MERCHANTS_CACHE]});
            queryClient.invalidateQueries({
                queryKey: [MERCHANT_DETAILS_CACHE],
            });
        },
    });
};

export {
    useGetMerchants,
    useGetInfiniteMerchants,
    useGetMerchantDetails,
    useUpdateMerchant,
    useToggleBlockMerchant,
};
